/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { BCP47Locale, CalendarUtils } from '../../UNSAFE_IntlDateTime';
import type { CalendarWidthNames } from '../../UNSAFE_IntlDateTime';
import type { CalendarDate, DatePlaceholders, IsoMonth } from '../../UNSAFE_InputDateMask';
import { checkIsValidIsoYear, checkNoCalendarExtension } from '#utils/UNSAFE_calendarDateUtils';

// The ^ an $ anchors ensure that the entire string is matched.
// The \d+ pattern ensures that the string consists entirely of digits.
// Previously the regexp was /\d/ . This tests that there is at least one match of the regular expression
//  in the string. This would return true for 'F10', and we do not want that.
const NUMBERS_ONLY_REGEXP = /^\d+$/;

/**
 * Replace placeholders in the masks array with their translated equivalents.
 */
const translateCustomMasks = (
  masks: DatePlaceholders,
  monthPlaceholder: string,
  dayPlaceholder: string,
  yearPlaceholder: string
): DatePlaceholders => {
  return masks.map((p: Intl.DateTimeFormatPart) => {
    switch (p.type) {
      case 'month':
        return { ...p, value: monthPlaceholder };
      case 'day':
        return { ...p, value: dayPlaceholder };
      case 'year':
        return { ...p, value: yearPlaceholder };
      default:
        return { ...p, value: p.value.trim() };
    }
  }) as DatePlaceholders;
};

/**
 * Return month names for the Gregorian calendar.
 */
const getMonthNamesGregorian = (
  locale: BCP47Locale,
  style: NonNullable<Intl.DateTimeFormatOptions['weekday']>
) => {
  const cal = CalendarUtils.getCalendar(locale, 'gregory');
  const _monthNamesFormatMap = {
    short: 'abbreviated',
    narrow: 'narrow',
    long: 'wide'
  } as const;
  const calType: NonNullable<CalendarWidthNames> = _monthNamesFormatMap[style];
  const months = cal.months['stand-alone'][calType];
  return months;
};

const cachedMonthNames = new Map<string, object>();

/**
 * Return month name for the given locale, month, year, and style (long | short | narrow).
 * If year is unknown, pass '*'. We'll have to figure out how to handle this when we add support
 * for multiple calendars, where the number of months in a year can vary. The year is unused for
 * Gregorian calendars because the number of months is always 12.
 */
const getMonthName = (
  locale: BCP47Locale,
  month: IsoMonth,
  year: number | '*',
  style: NonNullable<Intl.DateTimeFormatOptions['weekday']>
) => {
  if (year !== '*') {
    checkIsValidIsoYear(year);
  }
  checkNoCalendarExtension(locale);

  let monthNames = cachedMonthNames.get(`${locale}-${style}`);
  if (!monthNames) {
    monthNames = getMonthNamesGregorian(locale, style);
    cachedMonthNames.set(`${locale}-${style}`, monthNames);
  }
  const monthIndex = '' + month;
  return (monthNames as any)[monthIndex];
};

/**
 * Return year, month, and day for Today.
 */
const getTodayAsCalendarDate = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return { year, month, day };
};

/**
 * Return true if the specified string contains numbers only.
 */
const isNumberOnlyString = (s: string) => {
  return NUMBERS_ONLY_REGEXP.test(s);
};

/**
 * Return true if the date params represent 2 different calendar dates.
 */
const calendarDatesAreDifferent = (d1: CalendarDate | undefined, d2: CalendarDate | undefined) => {
  const d1_empty = !d1 || (!d1.day && !d1.month && !d1.year);
  const d2_empty = !d2 || (!d2.day && !d2.month && !d2.year);

  // If both dates are empty, return false.
  if (d1_empty && d2_empty) {
    return false;
  }

  // If one is empty and the other isn't, return true.
  if ((d1_empty && !d2_empty) || (!d1_empty && d2_empty)) {
    return true;
  }

  // Return true if any of the segment values don't match.
  const d1_day = d1?.day;
  const d1_month = d1?.month;
  const d1_year = d1?.year;
  const d2_day = d2?.day;
  const d2_month = d2?.month;
  const d2_year = d2?.year;
  if (d1_day !== d2_day || d1_month !== d2_month || d1_year !== d2_year) {
    return true;
  }

  return false;
};

const stringToNumber = (s: string) => {
  const num = +s;
  if (isNaN(num)) {
    throw new Error(`Cannot convert the string ${s} to a number`);
  }
  return num;
};

const numberToString = (n: number) => n.toString();
const withinRange = (n: number, min: number, max: number) => n >= min && n <= max;

export {
  calendarDatesAreDifferent,
  getMonthName,
  getTodayAsCalendarDate,
  isNumberOnlyString,
  numberToString,
  stringToNumber,
  translateCustomMasks,
  withinRange
};
